import Button from '@atlaskit/button';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  type AiSummaryInteraction,
  beginAiSummaryInteraction,
  getDatabaseContainerType,
  useAnalytics
} from '@databases/analytics';
import {
  AiSummaryFieldModal,
  type AiSummaryFieldModalProps,
  buildAiSummaryPrompt,
  useAiSummaryPreviewOptions,
  useIsAiSummaryFieldEnabled
} from '@databases/database-ai-fields';
import type { AiSummaryRunResult } from '@databases/database-ai-fields/run-ai-summary-field';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { useFlags } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { previewDatabaseAiField } from '@databases/rovo-bridge';
import { type AiSummaryOutputFormat } from '@databases/types/database';
import React, {
  type FunctionComponent,
  type ReactNode,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { dynamicImport } from '@canvas/dynamic-import';

import {
  DatabaseAiConfigModalContext,
  type DatabaseAiConfigModal
} from './database-ai-config-modal-context.ts';

export {
  type DatabaseAiConfigModal,
  useDatabaseAiConfigModal
} from './database-ai-config-modal-context.ts';

type AiSummaryIndicatorState =
  | { kind: 'idle' }
  | {
      kind: 'running';
      fieldId: string;
      startedAt: number;
      phase: 'preparing' | 'generating' | 'updating';
    }
  | { kind: 'ready'; fieldId: string; startedAt: number };

interface DatabaseAiConfigModalProviderProps {
  children: ReactNode;
  contentId: string;
}

const AI_SUMMARY_RUN_ERROR_FLAG_ID = 'ai-summary-run-error';

const AiSummaryFieldThinkingBar = lazy(() =>
  dynamicImport(
    () => import('@databases/database-ai-fields/summary/ai-summary-field-thinking-bar')
  ).then(({ AiSummaryFieldThinkingBar: ThinkingBar }) => ({ default: ThinkingBar }))
);

const aiSummaryThinkingBarFallbackStyles = xcss({
  position: 'fixed',
  bottom: '36px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '336px',
  maxWidth: 'calc(100vw - 32px)',
  zIndex: 'modal',
  padding: 'space.150',
  backgroundColor: 'elevation.surface.overlay',
  borderRadius: 'radius.small',
  boxShadow: 'elevation.shadow.overlay'
});

const AiSummaryFieldThinkingBarFallback = ({
  actionLabel,
  message,
  onAction
}: {
  actionLabel: string;
  message: string;
  onAction: () => void;
}) => (
  <Box
    aria-live="polite"
    role="status"
    testId="ai-summary-thinking-bar-fallback"
    xcss={aiSummaryThinkingBarFallbackStyles}
  >
    <Inline alignBlock="center" space="space.100" spread="space-between">
      <span>{message}</span>
      <Button appearance="subtle" onClick={onAction}>
        {actionLabel}
      </Button>
    </Inline>
  </Box>
);

export const DatabaseAiConfigModalProvider: FunctionComponent<
  DatabaseAiConfigModalProviderProps
> = ({ children, contentId }) => {
  const { formatMessage, locale } = useIntl();
  const { addFlag, clearFlags, hideFlag, showFlag } = useFlags();
  const analytics = useAnalytics();
  const [activeModal, setActiveModal] = useState<DatabaseAiConfigModal | null>(null);
  const [isPreviewMenuOpen, setIsPreviewMenuOpen] = useState(false);
  const [aiSummaryIndicatorState, setAiSummaryIndicatorState] =
    useState<AiSummaryIndicatorState>({ kind: 'idle' });
  const [isAiSummaryRunPending, setIsAiSummaryRunPending] = useState(false);
  const activeAiSummaryRunRef = useRef<{
    controller: AbortController;
    completion: Promise<AiSummaryRunResult>;
  } | null>(null);
  const previewGeneratedRef = useRef(false);
  const previewInteractionRef = useRef<AiSummaryInteraction | null>(null);
  const { activeStream } = useDatabaseStreamState();
  const { isSingleFieldStreaming } = useSnapshot(aiFirstCreationStreamingState);
  const isAiFieldsSummaryEnabled = useIsAiSummaryFieldEnabled();
  const previewOptions = useAiSummaryPreviewOptions(
    contentId,
    activeModal?.type === 'ai-summary-field' && isPreviewMenuOpen
  );

  const endPreviewResultView = useCallback(() => {
    previewInteractionRef.current?.endResultView();
    previewInteractionRef.current = null;
  }, []);

  useEffect(() => endPreviewResultView, [endPreviewResultView]);

  const closeAiConfigModal = useCallback(() => {
    endPreviewResultView();
    setIsPreviewMenuOpen(false);
    setActiveModal(null);
  }, [endPreviewResultView]);

  const openAiConfigModal = useCallback(
    (modal: DatabaseAiConfigModal) => {
      setIsPreviewMenuOpen(false);
      previewGeneratedRef.current = false;
      setActiveModal(modal);
      void analytics.sendAiFieldSetupModalOpenedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        fieldId: modal.props.fieldId,
        modalSessionId: globalThis.crypto.randomUUID(),
        aiFieldType: 'summary',
        openedFrom: modal.props.openedFrom
      });
    },
    [analytics, contentId]
  );

  const runAiSummaryWithIndicator = useCallback(
    ({
      fieldId,
      run
    }: {
      fieldId: string;
      run: (abortSignal: AbortSignal) => Promise<AiSummaryRunResult>;
    }) => {
      if (activeAiSummaryRunRef.current) {
        return { kind: 'blocked' } as const;
      }

      const controller = new AbortController();
      const startedAt = Date.now();
      setAiSummaryIndicatorState({
        kind: 'running',
        fieldId,
        startedAt,
        phase: 'preparing'
      });
      setIsAiSummaryRunPending(true);

      const completion = Promise.resolve()
        .then(() => run(controller.signal))
        .then((result) => {
          if (
            activeAiSummaryRunRef.current?.controller === controller &&
            !controller.signal.aborted
          ) {
            setAiSummaryIndicatorState(
              result.kind === 'generated'
                ? { kind: 'ready', fieldId, startedAt }
                : { kind: 'idle' }
            );
          }
          return result;
        })
        .catch((error: unknown) => {
          if (activeAiSummaryRunRef.current?.controller === controller) {
            setAiSummaryIndicatorState({ kind: 'idle' });
          }
          throw error;
        })
        .finally(() => {
          if (activeAiSummaryRunRef.current?.controller === controller) {
            activeAiSummaryRunRef.current = null;
            setIsAiSummaryRunPending(false);
          }
        });

      activeAiSummaryRunRef.current = { controller, completion };
      return { kind: 'started', completion } as const;
    },
    []
  );

  const stopAiSummaryRun = useCallback(() => {
    activeAiSummaryRunRef.current?.controller.abort();
    setAiSummaryIndicatorState({ kind: 'idle' });
  }, []);

  useEffect(
    () => () => {
      activeAiSummaryRunRef.current?.controller.abort();
      activeAiSummaryRunRef.current = null;
    },
    []
  );

  const showAiSummaryRunErrorFlag = useCallback(
    (
      modal: DatabaseAiConfigModal,
      settings: Parameters<NonNullable<AiSummaryFieldModalProps['onSaveAndRun']>>[0]
    ) => {
      const flagId = `${AI_SUMMARY_RUN_ERROR_FLAG_ID}:${Date.now()}`;
      const usesBridgeFlags = isFeatureFlagEnabled('database-frontend-flags-over-bridge');
      const dismissFlag = () => {
        if (usesBridgeFlags) {
          hideFlag(flagId);
        } else {
          clearFlags();
        }
      };
      const tryAgain = () => {
        void (async () => {
          try {
            const onSaveAndRun = modal.props.onSaveAndRun;
            if (onSaveAndRun) {
              const start = runAiSummaryWithIndicator({
                fieldId: modal.props.fieldId,
                run: (abortSignal) => onSaveAndRun(settings, abortSignal)
              });
              if (start.kind === 'blocked') {
                return;
              }
              await start.completion;
            }
            dismissFlag();
          } catch {
            // Keep the existing flag visible so the user can try again.
            return;
          }
        })();
      };
      const flag = {
        title: formatMessage(
          messages['field-settings.ai-summary.run-error-title.non-final']
        ),
        description: formatMessage(
          messages['field-settings.ai-summary.run-error-description.non-final']
        ),
        actions: [
          {
            content: formatMessage(
              messages['field-settings.ai-summary.run-error-try-again.non-final']
            ),
            onClick: tryAgain
          }
        ]
      };

      if (usesBridgeFlags) {
        showFlag({
          ...flag,
          id: flagId,
          type: 'error',
          appearance: 'normal',
          close: 'auto'
        });
      } else {
        addFlag({
          ...flag,
          appearance: 'normal',
          icon: () => (
            <StatusErrorIcon
              color={token('color.icon.danger', '#C9372C')}
              label=""
              spacing="spacious"
            />
          )
        });
      }
    },
    [addFlag, clearFlags, formatMessage, hideFlag, runAiSummaryWithIndicator, showFlag]
  );

  const isAiSummaryRunActive = isAiSummaryRunPending;
  const value = useMemo(
    () => ({
      openAiConfigModal,
      closeAiConfigModal,
      isAiSummaryRunActive,
      runAiSummaryWithIndicator
    }),
    [
      closeAiConfigModal,
      isAiSummaryRunActive,
      openAiConfigModal,
      runAiSummaryWithIndicator
    ]
  );
  const isMatchingAiSummaryStream =
    aiSummaryIndicatorState.kind !== 'idle' &&
    activeStream?.kind === 'ai-field' &&
    activeStream.target.aiFieldType === 'summary' &&
    activeStream.target.fieldId === aiSummaryIndicatorState.fieldId;
  useEffect(() => {
    setAiSummaryIndicatorState((currentState) => {
      if (currentState.kind !== 'running') {
        return currentState;
      }
      if (isMatchingAiSummaryStream) {
        const phase =
          isSingleFieldStreaming === false
            ? ('updating' as const)
            : ('generating' as const);
        return currentState.phase === phase ? currentState : { ...currentState, phase };
      }
      return currentState;
    });
  }, [isMatchingAiSummaryStream, isSingleFieldStreaming]);
  const visibleAiSummaryIndicator =
    aiSummaryIndicatorState.kind === 'idle'
      ? null
      : {
          phase:
            aiSummaryIndicatorState.kind === 'ready'
              ? ('ready' as const)
              : aiSummaryIndicatorState.phase,
          startedAt: aiSummaryIndicatorState.startedAt
        };
  const isAiSummaryIndicatorReady = visibleAiSummaryIndicator?.phase === 'ready';
  const closeAiSummaryIndicator = () => setAiSummaryIndicatorState({ kind: 'idle' });
  const aiSummaryIndicatorPortal =
    visibleAiSummaryIndicator && globalThis.document?.body
      ? createPortal(
          <Suspense
            fallback={
              <AiSummaryFieldThinkingBarFallback
                actionLabel={
                  isAiSummaryIndicatorReady
                    ? formatMessage(messages['close-label'])
                    : formatMessage(
                        messages[
                          'field-actions.ai-summary-rerun-confirmation-cancel.non-final'
                        ]
                      )
                }
                message={
                  isAiSummaryIndicatorReady
                    ? formatMessage(
                        messages['field-actions.ai-summary-thinking.ready.non-final']
                      )
                    : formatMessage(
                        messages['field-actions.ai-summary-thinking.preparing.non-final']
                      )
                }
                onAction={
                  isAiSummaryIndicatorReady ? closeAiSummaryIndicator : stopAiSummaryRun
                }
              />
            }
          >
            <AiSummaryFieldThinkingBar
              onClose={closeAiSummaryIndicator}
              onStop={stopAiSummaryRun}
              phase={visibleAiSummaryIndicator.phase}
              startedAt={visibleAiSummaryIndicator.startedAt}
            />
          </Suspense>,
          globalThis.document.body
        )
      : null;

  return (
    <DatabaseAiConfigModalContext.Provider value={value}>
      {children}
      {isAiFieldsSummaryEnabled && activeModal?.type === 'ai-summary-field' && (
        <AiSummaryFieldModal
          mode={activeModal.props.mode}
          initialConfig={activeModal.props.initialConfig}
          previewOptions={previewOptions}
          onPreviewMenuOpen={() => setIsPreviewMenuOpen(true)}
          onClose={({ config, exitAction }) => {
            void analytics.sendAiFieldSetupAbandonedEvent({
              contentId,
              databaseContainer: getDatabaseContainerType(),
              fieldId: activeModal.props.fieldId,
              aiFieldType: 'summary',
              exitAction,
              previewGenerated: previewGeneratedRef.current,
              outputFormat: toAnalyticsOutputFormat(config.outputFormat)
            });
            activeModal.props.onClose?.();
            closeAiConfigModal();
          }}
          onRegenerate={activeModal.props.onRegenerate}
          onGeneratePreview={async ({ abortSignal, config, entryId }) => {
            endPreviewResultView();
            const interaction = beginAiSummaryInteraction(
              contentId,
              `preview:${activeModal.props.fieldId}`,
              'aiFieldModal'
            );
            previewInteractionRef.current = interaction;
            const runId = globalThis.crypto.randomUUID();
            const eventData = {
              contentId,
              databaseContainer: getDatabaseContainerType(),
              fieldId: activeModal.props.fieldId,
              aiFieldType: 'summary' as const,
              runId,
              triggerType: 'preview' as const,
              operation: 'single-entry-generate' as const,
              runScope: 'singleEntry' as const,
              source: 'aiFieldModal' as const
            };
            void analytics.sendAiFieldRunStartedEvent(eventData);

            try {
              const result = await previewDatabaseAiField({
                abortSignal,
                contentId,
                entryId,
                fieldName: activeModal.props.fieldName,
                locale,
                traceContext: interaction.traceContext,
                prompt: buildAiSummaryPrompt({
                  operation: 'single-entry-generate',
                  targetFieldName: activeModal.props.fieldName,
                  config
                })
              });
              void analytics.sendAiFieldRunCompletedEvent(eventData);
              if (result === null && previewInteractionRef.current === interaction) {
                endPreviewResultView();
              }
              return result;
            } catch (error) {
              const errorCode =
                typeof error === 'object' &&
                error !== null &&
                'code' in error &&
                typeof error.code === 'string'
                  ? error.code
                  : undefined;
              void analytics.sendAiFieldRunFailedEvent({
                ...eventData,
                errorCategory: error instanceof Error ? error.name : 'unknown',
                errorCode
              });
              if (previewInteractionRef.current === interaction) {
                endPreviewResultView();
              }
              throw error;
            }
          }}
          onPreviewGenerated={(config) => {
            previewGeneratedRef.current = true;
            previewInteractionRef.current?.resultViewed();
            void analytics.sendAiFieldPreviewGeneratedEvent({
              contentId,
              databaseContainer: getDatabaseContainerType(),
              fieldId: activeModal.props.fieldId,
              aiFieldType: 'summary',
              outputFormat: toAnalyticsOutputFormat(config.outputFormat)
            });
          }}
          onPreviewCleared={endPreviewResultView}
          isSaveAndRunDisabled={isAiSummaryRunActive}
          onSaveAndRun={(settings) => {
            const modal = activeModal;
            const onSaveAndRun = modal.props.onSaveAndRun;
            if (!onSaveAndRun) {
              closeAiConfigModal();
              sendSavedAnalytics(analytics, contentId, modal, settings);
              return;
            }

            const start = runAiSummaryWithIndicator({
              fieldId: modal.props.fieldId,
              run: (abortSignal) => onSaveAndRun(settings, abortSignal)
            });
            if (start.kind === 'blocked') {
              return;
            }

            closeAiConfigModal();
            sendSavedAnalytics(analytics, contentId, modal, settings);

            void (async () => {
              try {
                await start.completion;
              } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                  return;
                }
                return showAiSummaryRunErrorFlag(modal, settings);
              }
            })();
          }}
          onSaveWithoutRunning={(settings) => {
            activeModal.props.onSaveWithoutRunning?.(settings);
            sendSavedAnalytics(analytics, contentId, activeModal, settings);
            closeAiConfigModal();
          }}
        />
      )}
      {aiSummaryIndicatorPortal}
    </DatabaseAiConfigModalContext.Provider>
  );
};

const toAnalyticsOutputFormat = (outputFormat: AiSummaryOutputFormat) =>
  outputFormat === 'one-liner'
    ? ('oneLiner' as const)
    : outputFormat === 'bullet-points'
      ? ('bulletPoints' as const)
      : ('paragraph' as const);

const sendSavedAnalytics = (
  analytics: ReturnType<typeof useAnalytics>,
  contentId: string,
  modal: DatabaseAiConfigModal,
  settings: Parameters<NonNullable<AiSummaryFieldModalProps['onSaveWithoutRunning']>>[0]
) => {
  const eventData = {
    contentId,
    databaseContainer: getDatabaseContainerType(),
    fieldId: modal.props.fieldId,
    aiFieldType: 'summary' as const
  };

  if (modal.props.mode === 'add') {
    void analytics.sendAiFieldCreatedEvent(eventData);
    return;
  }

  const previousFormat = modal.props.initialConfig?.outputFormat ?? 'paragraph';
  if (previousFormat !== settings.outputFormat) {
    void analytics.sendAiFieldSettingsUpdatedEvent({
      ...eventData,
      format: toAnalyticsOutputFormat(settings.outputFormat),
      previousFormat: toAnalyticsOutputFormat(previousFormat)
    });
  }
};
